import React, { useState } from 'react';
import {
  Edit2,
  Eye,
  Copy,
  Send,
  Plus,
  Check,
  X,
} from 'lucide-react';
import { STAGE_COLOURS, STAGE_LABELS } from '../lib/sampleData';

/**
 * TemplateManager Component
 * Manages email templates with full CRUD operations.
 * Templates grouped by category: Event Templates and Wedding Templates.
 */
const TemplateManager = ({
  emailTemplates = [],
  setEmailTemplates,
  events = [],
  venues = [],
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [previewId, setPreviewId] = useState(null);

  // Group templates by category
  const eventTemplates = (emailTemplates || []).filter(
    (t) => !t.id.includes('wedding')
  );
  const weddingTemplates = (emailTemplates || []).filter(
    (t) => t.id.includes('wedding')
  );

  // Get stage color
  const getStageColor = (stage) => {
    const hex = STAGE_COLOURS[stage];
    return hex || '#10B981';
  };

  const getStageLabel = (stage) => {
    return STAGE_LABELS[stage] || stage;
  };

  // Enable/Disable toggle
  const toggleEnabled = (templateId) => {
    if (setEmailTemplates) {
      setEmailTemplates(
        emailTemplates.map((t) =>
          t.id === templateId ? { ...t, enabled: !t.enabled } : t
        )
      );
    }
  };

  // Edit mode
  const startEdit = (template) => {
    setEditingId(template.id);
    setEditFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
    });
  };

  const saveEdit = (templateId) => {
    if (setEmailTemplates) {
      setEmailTemplates(
        emailTemplates.map((t) =>
          t.id === templateId
            ? {
                ...t,
                ...editFormData,
              }
            : t
        )
      );
    }
    setEditingId(null);
    setEditFormData({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  // Clone template
  const cloneTemplate = (template) => {
    if (setEmailTemplates) {
      const newTemplate = {
        ...template,
        id: `${template.id}_copy_${Date.now()}`,
        name: `${template.name} (Copy)`,
      };
      setEmailTemplates([...emailTemplates, newTemplate]);
    }
  };

  // Add new template
  const addNewTemplate = () => {
    if (setEmailTemplates) {
      const newTemplate = {
        id: `custom_${Date.now()}`,
        name: 'New Template',
        subject: '',
        body: '',
        stage: 'enquiry',
        enabled: true,
      };
      setEmailTemplates([...emailTemplates, newTemplate]);
    }
  };

  // Render template card
  const renderTemplateCard = (template) => {
    const isEditing = editingId === template.id;
    const stageColor = getStageColor(template.stage);
    const stageLabel = getStageLabel(template.stage);

    return (
      <div
        key={template.id}
        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
      >
        {isEditing ? (
          // Edit mode
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                checked={template.enabled !== false}
                onChange={() => toggleEnabled(template.id)}
                className="w-4 h-4 text-emerald-600 rounded"
              />
              <input
                type="text"
                value={editFormData.name || ''}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-medium"
                placeholder="Template name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={editFormData.subject || ''}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, subject: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                placeholder="Email subject line"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Body
              </label>
              <textarea
                value={editFormData.body || ''}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, body: e.target.value })
                }
                rows="8"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-mono"
                placeholder="Email body (use {VARIABLE_NAME} for placeholders)"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => saveEdit(template.id)}
                className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <Check size={16} />
                Save
              </button>
              <button
                onClick={cancelEdit}
                className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // View mode
          <>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    checked={template.enabled !== false}
                    onChange={() => toggleEnabled(template.id)}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <h3 className="font-bold text-gray-900">
                    {template.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Subject: {template.subject}
                </p>
                <div className="flex items-center gap-2">
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: stageColor }}
                  >
                    {stageLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 pt-3 border-t border-gray-200">
              <button
                onClick={() => startEdit(template)}
                title="Edit template"
                className="p-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex-1 flex items-center justify-center gap-2 text-sm"
              >
                <Edit2 size={16} />
                Edit
              </button>
              <button
                onClick={() =>
                  setPreviewId(previewId === template.id ? null : template.id)
                }
                title="Preview template"
                className="p-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex-1 flex items-center justify-center gap-2 text-sm"
              >
                <Eye size={16} />
                Preview
              </button>
              <button
                onClick={() => cloneTemplate(template)}
                title="Clone template"
                className="p-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex-1 flex items-center justify-center gap-2 text-sm"
              >
                <Copy size={16} />
                Clone
              </button>
              <button
                title="Test send"
                className="p-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex-1 flex items-center justify-center gap-2 text-sm"
              >
                <Send size={16} />
                Test
              </button>
            </div>

            {/* Preview section */}
            {previewId === template.id && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">
                  Preview
                </p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-gray-600 uppercase font-semibold mb-1">
                      Subject
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {template.subject}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 uppercase font-semibold mb-1">
                      Body
                    </p>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono">
                      {template.body}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Templates</h2>
          <p className="text-gray-600 text-sm mt-1">
            Manage email templates for different event stages
          </p>
        </div>
        <button
          onClick={addNewTemplate}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center gap-2 text-sm"
        >
          <Plus size={18} />
          New Template
        </button>
      </div>

      {/* Event Templates Section */}
      <div className="mb-10">
        <div className="bg-gray-50 px-4 py-3 rounded-lg mb-4">
          <h3 className="font-bold text-gray-900 text-sm uppercase">
            Event Templates
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Standard event booking workflow templates
          </p>
        </div>
        <div className="space-y-3">
          {eventTemplates.length > 0 ? (
            eventTemplates.map(renderTemplateCard)
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No event templates yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Wedding Templates Section */}
      <div>
        <div className="bg-gray-50 px-4 py-3 rounded-lg mb-4">
          <h3 className="font-bold text-gray-900 text-sm uppercase">
            Wedding Templates
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Wedding-specific booking and planning templates
          </p>
        </div>
        <div className="space-y-3">
          {weddingTemplates.length > 0 ? (
            weddingTemplates.map(renderTemplateCard)
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No wedding templates yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;
